import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Modal } from "../../components/modal";
import { formValuesToResidencyName } from "../../components/residency-name-input";
import { SubletForm, SubletFormValues } from "../../components/sublet-form";
import { useAdditionalFields } from "../../hooks/addition-fields";
import {
  additionalFieldDefaultFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { createSublet } from "../../services/sublet";
import { navigateBack } from "../../utils/navigate-back";

export function SubletCreate() {
  const navigate = useNavigate();
  const { data: additionalFields } = useAdditionalFields("sublet");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<SubletFormValues>({
    parentId: "",
    fromDate: "",
    toDate: "",
    useCustomName: false,
    customName: "",
    tenants: [
      {
        personId: "",
      },
    ],
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: additionalFieldDefaultFormValue(field.type),
      }),
      {},
    ),
  });

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/sublet");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <SubletForm
        hasError={hasError}
        loading={loading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await createSublet({
                ...(formValues.parentId.startsWith("holding:")
                  ? { holdingId: formValues.parentId }
                  : { leaseId: formValues.parentId }),
                tenants: formValues.tenants,
                fromDate: new Date(formValues.fromDate),
                toDate: formValues.toDate
                  ? new Date(formValues.toDate)
                  : undefined,
                customName: formValuesToResidencyName(formValues) ?? undefined,
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                  ),
                }),
              });
              await queryClient.invalidateQueries({ queryKey: ["sublets"] });
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
